import {
  type ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useMemo, useState } from 'react';
import { DataTable } from '@/components/custom/data-table';
import { Button } from '@/components/ui/button';

export interface WidgetColumn<T> {
  key: string;
  header: string;
  /** Right-aligns and renders tabular numerals. */
  numeric?: boolean;
  /** Cell contents. */
  render: (row: T) => ReactNode;
  /**
   * Value to sort by. Omit to make the column unsortable — useful for columns
   * whose cell is purely presentational.
   */
  sortValue?: (row: T) => string | number;
  /** Column width in px; feeds the shared table's `table-fixed` sizing. */
  size?: number;
}

interface Props<T> {
  rows: T[];
  columns: WidgetColumn<T>[];
  /** Column sorted on first render, descending. */
  defaultSortKey?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  /** Rows per page. Omit to show everything on one page. */
  pageSize?: number;
}

/**
 * The table half of every "chart or table" widget, and the table view of every
 * drilldown.
 *
 * Composes the app's shared `<DataTable>` (TanStack Table + shadcn `Table` +
 * `StickyTableScroll`), so widget tables get the same sticky header, column
 * sizing and sort affordances as every list page instead of a second
 * implementation.
 *
 * The one difference from a list page: sorting and pagination run
 * **client-side**. That is correct here — a widget renders an already-aggregated,
 * bounded result set (locations, roles, the people behind one tile), not a
 * server-paged collection. G13's "paginate and sort on the server" governs list
 * pages, which fetch a page at a time; nothing here does.
 */
export function WidgetTable<T>({
  rows,
  columns,
  defaultSortKey,
  onRowClick,
  emptyMessage = 'Nothing to show.',
  pageSize,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(
    defaultSortKey ? [{ id: defaultSortKey, desc: true }] : [],
  );

  const columnDefs = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((c) => ({
        id: c.key,
        header: c.header,
        size: c.size ?? (c.numeric ? 110 : 150),
        enableSorting: Boolean(c.sortValue),
        // Sort on the declared value rather than on whatever markup the cell
        // renders, so a badge or a formatted number still orders correctly.
        accessorFn: (row: T) => c.sortValue?.(row) ?? '',
        cell: ({ row }) => (
          <span className={c.numeric ? 'block text-right tabular-nums' : undefined}>
            {c.render(row.original)}
          </span>
        ),
      })),
    [columns],
  );

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pageSize
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize } },
        }
      : {}),
  });

  const showFooter = Boolean(pageSize) && rows.length > (pageSize ?? 0);
  const { pageIndex, pageSize: currentSize } = table.getState().pagination;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {/* `StickyTableScroll` inside DataTable is `flex-1 min-h-0 overflow-auto`,
          so it only becomes a scrollport when its parent is a bounded flex
          column. Without this the table grew past the panel and got clipped
          with no way to scroll. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable
          table={table}
          isLoading={false}
          isError={false}
          errorMessage=""
          emptyMessage={emptyMessage}
          onRowClick={onRowClick}
        />
      </div>
      {showFooter ? (
        <footer className="flex shrink-0 items-center justify-between text-muted-foreground text-xs">
          <span className="tabular-nums">
            Showing {pageIndex * currentSize + 1}–
            {Math.min((pageIndex + 1) * currentSize, rows.length)} of {rows.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </footer>
      ) : null}
    </div>
  );
}
